// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/ai-tools.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * The model's tools, generated from the agent capabilities so the two cannot
 * drift (docs/AI_CHATBOT_PLAN.md §4). Each tool is an existing AgentCommand;
 * its JSON Schema mirrors validateAgentCommand, which still checks every call
 * before it runs. OpenAI-style function names cannot contain dots, so
 * `view.goto` is offered as `view_goto`.
 */
import { AGENT_MAX_ROTATION_SPEED, AGENT_MAX_ZOOM, validateAgentCommand } from './agent-capabilities';
import type { AgentCapability, AgentCommand } from './protocol';

type JsonSchema = Record<string, unknown>;
type AgentCommandType = AgentCommand['type'];

const latitude: JsonSchema = { type: 'number', minimum: -90, maximum: 90, description: 'Latitude in degrees, north positive.' };
const longitude: JsonSchema = { type: 'number', minimum: -180, maximum: 180, description: 'Longitude in degrees, east positive.' };
const point: JsonSchema = {
  type: 'object',
  properties: { lat: latitude, lon: longitude },
  required: ['lat', 'lon'],
  additionalProperties: false
};
const bounds: JsonSchema = {
  type: 'object',
  description: 'A box in degrees; w > e crosses the antimeridian. Leave out for the whole globe.',
  properties: { n: latitude, s: latitude, w: longitude, e: longitude },
  required: ['n', 's', 'w', 'e'],
  additionalProperties: false
};
const noArguments: JsonSchema = { type: 'object', properties: {}, additionalProperties: false };
const pin: JsonSchema = { type: 'string', minLength: 1, maxLength: 100, description: 'A pin id from pins_read, e.g. "pin-3".' };
const yearSpan: JsonSchema = {
  type: 'object',
  description: 'Whole years, from before to.',
  properties: { from: { type: 'integer' }, to: { type: 'integer' } },
  required: ['from', 'to'],
  additionalProperties: false
};

function object(properties: Record<string, JsonSchema>, required: string[] = []): JsonSchema {
  return { type: 'object', properties, required, additionalProperties: false };
}

/**
 * Every agent command's arguments, as the model sees them, keyed by command
 * name. Shared by both apps, so it covers the union of their commands; each
 * app offers only the capabilities it advertises (AYNI 2D has no
 * sphere.slice), and its own validator still rules on every call.
 */
export const AGENT_TOOL_INPUT_SCHEMAS: Readonly<Record<string, JsonSchema>> = {
  'catalog.search': object({
    query: { type: 'string', minLength: 1, maxLength: 200, description: 'English keywords (the catalog is in English), e.g. "hurricane".' },
    categories: { type: 'array', items: { type: 'string' } },
    limit: { type: 'integer', minimum: 1, maximum: 50 }
  }, ['query']),
  'dataset.load': object({
    datasetId: { type: 'string', minLength: 1, description: 'An id returned by catalog_search. Never invent one.' }
  }, ['datasetId']),
  'view.goto': object({
    longitude,
    latitude,
    zoom: { type: 'number', exclusiveMinimum: 0, maximum: AGENT_MAX_ZOOM, description: 'Optional zoom; 1 is the whole sphere.' }
  }, ['longitude', 'latitude']),
  'view.zoom': object({ zoom: { type: 'number', exclusiveMinimum: 0, maximum: AGENT_MAX_ZOOM, description: '1 is the whole sphere; larger zooms in.' } }, ['zoom']),
  'view.rotation': object({
    enabled: { type: 'boolean', description: 'true starts the sphere turning by itself; false stops it.' },
    speed: { type: 'number', minimum: 0, maximum: AGENT_MAX_ROTATION_SPEED, description: 'Optional turning speed; the usual speed is about 7.' }
  }, ['enabled']),
  'media.seekDate': object({
    date: { type: 'string', description: 'ISO 8601 date or date-time, e.g. "2026-09-20" or "2026-09-20T12:00Z".' }
  }, ['date']),
  'media.play': noArguments,
  'media.pause': noArguments,
  'media.stop': noArguments,
  'media.seek': object({ time: { type: 'number', minimum: 0, description: 'Seconds from the start of the media.' } }, ['time']),
  'media.speed': object({ speed: { type: 'number', exclusiveMinimum: 0, maximum: 10 } }, ['speed']),
  'sphere.slice': object({ count: { type: 'integer', enum: [1, 2, 3, 4] } }, ['count']),
  'state.describe': noArguments,
  'luma.describe': noArguments,
  'luma.palette': object({ palette: { type: 'string', minLength: 1 } }, ['palette']),
  'luma.stretch': object({
    lo: { type: 'number' },
    hi: { type: 'number' },
    reset: { type: 'boolean', description: 'true returns to the published range; then leave out lo and hi.' }
  }),
  'luma.probe': object({ lat: latitude, lon: longitude }, ['lat', 'lon']),
  'luma.probeMany': object({ points: { type: 'array', items: point, minItems: 1, maxItems: 64 } }, ['points']),
  'luma.stats': object({
    bounds,
    threshold: { type: 'number', description: 'Also report the area above this value.' },
    includeHistogram: { type: 'boolean' }
  }),
  'luma.extremum': object({ kind: { type: 'string', enum: ['max', 'min'] }, bounds }),
  'luma.transect': object({ from: point, to: point, samples: { type: 'integer', minimum: 2 } }, ['from', 'to']),
  'luma.zonal': object({ bounds, maxRows: { type: 'integer', minimum: 2 } }),
  'pins.place': object({
    points: { type: 'array', items: point, minItems: 1, maxItems: 4, description: 'Where the visitor asked for pins.' },
    replace: { type: 'boolean', description: 'true removes the existing pins first.' }
  }, ['points']),
  'pins.read': noArguments,
  'pins.clear': noArguments,
  'pins.extremes': noArguments,
  // AYNI XR's Research and Education environments (off in AYNI 3D and 2D).
  'stats.trend': object({ pin }, ['pin']),
  'stats.emergence': object({
    pin,
    baseline: yearSpan,
    noise: { type: 'number', exclusiveMinimum: 0, description: 'Optional noise level in the data\'s units; leave out to use the baseline\'s own.' }
  }, ['pin']),
  'stats.correlation': object({ pinA: pin, pinB: pin }, ['pinA', 'pinB']),
  'baseline.hold': object({
    frame: { type: 'string', enum: ['current'], description: 'Hold the frame on screen. Give this or period, not both.' },
    // A plain object rather than anyOf, which small models handle badly:
    // {} is the default period, normalised to 'default' by the validator.
    period: {
      type: 'object',
      description: 'A period of whole years {from, to}; {} for the standard 30-year period. Give this or frame, not both.',
      properties: { from: { type: 'integer' }, to: { type: 'integer' } },
      additionalProperties: false
    }
  }),
  'baseline.status': noArguments,
  'baseline.clear': noArguments,
  'change.show': object({ mode: { type: 'string', enum: ['off', 'difference', 'percent', 'sigma'] } }, ['mode']),
  'compare.run': object({
    source: { type: 'string', enum: ['overlay', 'globe'] },
    id: { type: 'string', minLength: 1, maxLength: 100, description: 'Which overlay or globe, when there is more than one.' },
    pairing: { type: 'string', enum: ['time', 'shown'], description: "'time' pairs frames by date; 'shown' compares what is on screen." },
    mode: { type: 'string', enum: ['difference', 'percent'] },
    bounds
  }, ['source']),
  'threshold.area': object({
    mode: { type: 'string', enum: ['value', 'difference', 'sigma'], description: "'value' the data itself; 'difference' the change from the baseline; 'sigma' the change in σ." },
    comparator: { type: 'string', enum: ['above', 'below'] },
    threshold: { type: 'number', description: "In the data's units, or σ for 'sigma'." },
    bounds
  }, ['mode', 'comparator', 'threshold']),
  'hovmoller.summary': object({ from: point, to: point }),
  'methods.get': noArguments,
  'figure.open': object({ kind: { type: 'string', enum: ['globe', 'equal-earth'] } }),
  'bundle.open': noArguments,
  'cite.view': object({ format: { type: 'string', enum: ['text', 'bibtex', 'ris'] } }),
  'lesson.find': object({
    query: { type: 'string', minLength: 1, maxLength: 200, description: 'English words, e.g. "weather climate".' },
    grade: { type: 'integer', minimum: 0, maximum: 13, description: '0 is kindergarten.' },
    standard: { type: 'string', minLength: 1, maxLength: 100, description: 'A standard code, e.g. "MS-ESS2-5".' },
    unit: { type: 'string', minLength: 1, maxLength: 100 },
    limit: { type: 'integer', minimum: 1, maximum: 10 }
  }),
  'lesson.open': object({ slug: { type: 'string', minLength: 1, maxLength: 100, description: 'A slug returned by lesson_find. Never invent one.' } }, ['slug']),
  'lesson.context': noArguments,
  'lesson.hint': object({ step: { type: 'integer', minimum: 1, description: 'Leave out for the current step.' } }),
  'misconception.check': object({ claim: { type: 'string', minLength: 1, maxLength: 500, description: "The student's idea, in their words." } }, ['claim']),
  'explain.value': object({ lat: latitude, lon: longitude }),
  // DSA 1.8 vector layers (off in AYNI 3D and 2D until they carry it out).
  'features.query': object({
    layer: { type: 'string', minLength: 1, maxLength: 100, description: 'The layer\'s name, only when the globe draws more than one.' },
    when: { type: 'string', enum: ['shown', 'all'], description: "'shown' (default): the features drawn for the date on screen. 'all': the whole period." },
    from: { type: 'string', description: 'Only features from this ISO date on, e.g. "2026-09-01" (implies all).' },
    to: { type: 'string', description: 'Only features up to this ISO date, inclusive (implies all).' },
    bounds,
    sortBy: { type: 'string', minLength: 1, maxLength: 100, description: 'A numeric field to rank by, e.g. "mag" or "depth". Leave out for the latest first.' },
    order: { type: 'string', enum: ['desc', 'asc'], description: "'desc' (default): largest first; 'asc': smallest first." },
    where: {
      type: 'array',
      maxItems: 4,
      description: 'Conditions on fields, all of which must hold.',
      items: object({
        field: { type: 'string', minLength: 1, maxLength: 100 },
        min: { type: 'number', description: 'At least this.' },
        max: { type: 'number', description: 'At most this.' },
        equals: { type: ['string', 'number', 'boolean'] }
      }, ['field'])
    },
    limit: { type: 'integer', minimum: 1, maximum: 10, description: 'How many features to list (default 5).' }
  }),
  // The Satellites layer and the catalog (off in AYNI 3D and 2D, which have no Satellites layer).
  'satellites.sources': object({
    date: { type: 'string', description: 'The satellites of this ISO date\'s period, e.g. "1985-06-01". Leave out for the frame on screen.' }
  }),
  'satellites.datasets': object({
    satellite: { type: 'string', minLength: 1, maxLength: 100, description: 'A satellite or mission name, e.g. "Sentinel-1" or "GOES-19", or a NORAD id.' }
  }, ['satellite']),
  'satellites.show': object({
    on: { type: 'boolean', description: 'false hides the layer. Leave out to show it.' },
    norad: { type: 'integer', minimum: 1, description: 'The NORAD id of the satellite to select, from satellites_sources or satellites_datasets.' },
    fov: { type: 'boolean', description: 'Draw what the live satellites see (true) or not (false).' }
  }),
  'satellites.passes': noArguments,
  'satellites.events': object({
    open: { type: 'string', minLength: 1, maxLength: 100, description: 'An event id from an earlier satellites_events answer, to show it on the globe.' },
    limit: { type: 'integer', minimum: 1, maximum: 10, description: 'How many events to list (default 5).' }
  }),
  // Khipus (off unless the app plays them).
  'khipu.list': object({
    query: { type: 'string', maxLength: 200, description: 'Words to match, e.g. "ocean" or "storms". Leave out for every Khipu.' },
    limit: { type: 'integer', minimum: 1, maximum: 10 }
  }),
  'khipu.open': object({
    khipuId: { type: 'string', minLength: 1, maxLength: 100, description: 'An id returned by khipu_list. Never invent one.' },
    step: { type: 'integer', minimum: 1, description: 'Start at this step (1-based); leave out for the first.' }
  }, ['khipuId']),
  'khipu.step': object({
    to: { type: ['string', 'integer'], description: '"next", "previous", or a step number (1-based).' }
  }, ['to']),
  'khipu.close': noArguments
};

export interface AiToolDefinition {
  type: 'function';
  function: { name: string; description: string; parameters: JsonSchema };
}

export function agentCommandToToolName(type: AgentCommandType): string {
  return type.replace(/\./g, '_');
}

const TOOL_NAME_TO_COMMAND = new Map<string, string>(
  Object.keys(AGENT_TOOL_INPUT_SCHEMAS).map((type) => [type.replace(/\./g, '_'), type])
);

/** The command a tool name stands for (whether this app has it is the validator's call). */
export function toolNameToAgentCommandType(name: string): string | null {
  return TOOL_NAME_TO_COMMAND.get(name) ?? null;
}

/**
 * The capabilities as tools. Without `only`, the enabled ones. With `only`,
 * exactly the listed ones: a host's own list (a kiosk's slice-scoped set,
 * AYNI XR's commands) may name a capability this app keeps off by default,
 * such as the measurement pins, because that host carries it out.
 */
export function buildAiToolDefinitions(
  capabilities: ReadonlyArray<AgentCapability>,
  only?: ReadonlySet<AgentCommandType>
): AiToolDefinition[] {
  return capabilities
    .filter((capability) => (only ? only.has(capability.name) : capability.enabled) && capability.name in AGENT_TOOL_INPUT_SCHEMAS)
    .map((capability) => ({
      type: 'function' as const,
      function: {
        name: agentCommandToToolName(capability.name),
        description: capability.description,
        parameters: AGENT_TOOL_INPUT_SCHEMAS[capability.name] ?? { type: 'object' }
      }
    }));
}

/**
 * A model's tool call as a validated AgentCommand, or the reason it is not
 * one, worded for the model to correct itself.
 */
export function toolCallToAgentCommand(
  name: string,
  argumentsJson: string,
  allowed?: ReadonlySet<AgentCommandType>
): { ok: true; command: AgentCommand } | { ok: false; message: string } {
  const type = toolNameToAgentCommandType(name);
  if (!type || (allowed && !allowed.has(type as AgentCommandType))) {
    return { ok: false, message: `There is no tool called ${name} here.` };
  }
  let args: unknown = {};
  if (argumentsJson.trim()) {
    try {
      args = JSON.parse(argumentsJson);
    } catch {
      return { ok: false, message: `The arguments for ${name} are not valid JSON.` };
    }
  }
  if (typeof args !== 'object' || args === null || Array.isArray(args)) {
    return { ok: false, message: `The arguments for ${name} must be a JSON object.` };
  }
  const validation = validateAgentCommand({ ...(args as Record<string, unknown>), type });
  return validation.ok ? { ok: true, command: validation.command } : { ok: false, message: validation.error.message };
}
